import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  Loader2, 
  ArrowLeft,
  Shield,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { roleApiService, RoleCreateRequest, RoleUpdateRequest, RoleWithPermissionsResponse } from '../services/roleApi';
import { Menu } from '../types/user';
import { RoleMenuPermission } from '../services/roleApi';
import { MenuPermissions } from './MenuPermissions';

export function RoleForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = window.location.pathname;
  
  // Determine the mode: create, edit, or view
  const isCreateMode = id === 'new';
  const isEditMode = !isCreateMode && location.includes('/edit');
  const isViewMode = !isCreateMode && location.includes('/view');
  
  // console.log('🔧 RoleForm Mode:', { isCreateMode, isEditMode, isViewMode, location, id });
  
  const [formData, setFormData] = useState<RoleCreateRequest>({
    name: '',
    description: '',
    isActive: true
  });
  
  const [menus, setMenus] = useState<Menu[]>([]);
  const [permissions, setPermissions] = useState<RoleMenuPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load menus on component mount
  useEffect(() => {
    loadMenus();
  }, []);

  // Load role data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      loadRoleData();
    }
  }, [isEditMode, isViewMode, id]);

  // Debug: Monitor formData changes
  useEffect(() => {
    // console.log('🔧 Form data changed:', formData);
  }, [formData]);

  // Debug: Monitor menus and permissions
  useEffect(() => {
    // console.log('🔧 Menus and permissions changed:', { menusCount: menus.length, permissionsCount: permissions.length, isViewMode });
  }, [menus, permissions, isViewMode]);

  const loadMenus = async () => {
    try {
      // console.log('🔧 Loading menus...');
      const response = await roleApiService.getMenus(true);
      // console.log('🔧 Menus response:', response);
      if (response.success && response.data) {
        setMenus(response.data);
        // console.log('🔧 Menus loaded:', response.data.length, 'menus');
      } else {
        // console.error('🔧 Failed to load menus:', response);
        toast.error('Failed to load menus');
      }
    } catch (error) {
      // console.error('🔧 Error loading menus:', error);
      toast.error('Failed to load menus');
    }
  };

  const loadRoleData = async () => {
    try {
      setLoading(true);
      // console.log('🔧 Loading role data for ID:', id);
      
      // Ensure menus are loaded first
      if (menus.length === 0) {
        // console.log('🔧 Menus not loaded yet, loading menus first...');
        await loadMenus();
      }
      
      const response = await roleApiService.getRoleWithPermissions(id!);
      // console.log('🔧 API Response:', response);
      
      if (response.success && response.data) {
        const role = response.data.role;  // Access the nested role object
        const menuPermissions = response.data.menuPermissions;  // Access permissions directly
        
        // console.log('🔧 Role data received:', role);
        // console.log('🔧 Role name:', role.name);
        // console.log('🔧 Role description:', role.description);
        // console.log('🔧 Role isActive:', role.isActive);
        // console.log('🔧 Menu permissions count:', menuPermissions?.length || 0);
        
        setFormData({
          name: role.name || '',
          description: role.description || '',
          isActive: role.isActive ?? true
        });
        setPermissions(menuPermissions || []);
        
        console.log('🔧 Form data set to:', {
          name: role.name,
          description: role.description,
          isActive: role.isActive
        });
      } else {
        // console.error('🔧 API response not successful:', response);
        toast.error(response.message || 'Failed to load role');
        navigate('/roles');
      }
    } catch (error) {
      // console.error('🔧 Error loading role:', error);
      toast.error('Failed to load role');
      navigate('/roles');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof RoleCreateRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Role name is required');
      return false;
    }
    if (!formData.description || !formData.description.trim()) {
      toast.error('Role description is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      if (isEditMode && id) {
        // Update existing role with permissions
        const updateData = {
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive,
          assignedBy: 1, // TODO: Replace with actual current user ID from user context
          menuPermissions: permissions
        };
        
        // console.log('🔧 Updating role with data:', updateData);
        await roleApiService.updateRoleWithPermissions(id, updateData);
        toast.success('Role updated successfully!');
      } else {
        // Create new role with permissions
        const createData = {
          ...formData,
          assignedBy: 1, // TODO: Replace with actual current user ID from user context
          menuPermissions: permissions
        };
        
        // console.log('🔧 Creating role with data:', createData);
        await roleApiService.createRoleWithPermissions(createData);
        toast.success('Role created successfully!');
      }

      navigate('/roles');
    } catch (error: any) {
      // console.error('Error saving role:', error);
      
      // Show more specific error message
      if (error.response?.data?.message) {
        toast.error(`Failed to save role: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Failed to save role: ${error.message}`);
      } else {
        toast.error('Failed to save role');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/roles');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading role...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={handleCancel}
          className="p-2"
        >
          <FileText className="h-5 w-5 text-primary-700" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isCreateMode ? 'Create New Role' : isEditMode ? 'Edit Role' : 'View Role'}
          </h1>
          <p className="text-gray-600">
            {isCreateMode ? 'Add a new role to the system' : isEditMode ? 'Update role information and permissions' : 'View role information and permissions'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card className={isViewMode ? 'bg-gray-50' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" />
            Role Information
            {isViewMode && (
              <span className="text-sm font-normal text-gray-500 ml-2">(Read Only)</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Role Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Role Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter role name (e.g., Manager, User, Admin)"
              className="max-w-md"
              disabled={isViewMode}
            />
            <p className="text-sm text-gray-500">
              Choose a descriptive name for the role
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the role's responsibilities and permissions"
              rows={3}
              className="max-w-2xl"
              disabled={isViewMode}
            />
            <p className="text-sm text-gray-500">
              Provide a clear description of what this role can do
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => handleInputChange('isActive', checked)}
              disabled={isViewMode}
            />
            <Label htmlFor="isActive">Active</Label>
            <p className="text-sm text-gray-500 ml-2">
              Inactive roles cannot be assigned to users
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Menu Permissions */}
      <MenuPermissions
        menus={menus}
        permissions={permissions}
        onPermissionsChange={setPermissions}
        disabled={isViewMode}
      />

      {/* Action Buttons */}
      {isViewMode ? (
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={handleCancel}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Roles
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditMode ? 'Update Role' : 'Create Role'}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
